<template>
  <div class="p-6 space-y-6" style="max-width:720px;">
    <div class="page-header">
      <div>
        <h1 class="page-title">個人設定</h1>
        <p class="page-subtitle">管理您的帳號資訊與偏好設定</p>
      </div>
    </div>

    <!-- Account Info Card -->
    <div class="card p-6 space-y-5">
      <div class="font-semibold text-sm" style="color:var(--gray-500);letter-spacing:.05em;text-transform:uppercase;">帳號資訊</div>

      <!-- Avatar + Email row -->
      <div class="flex items-center gap-4">
        <div class="flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white"
             style="background: linear-gradient(135deg, var(--brand-500), var(--brand-700));">
          {{ avatarInitial }}
        </div>
        <div>
          <div class="font-semibold text-lg" style="color:var(--gray-900);">{{ currentUser?.username || currentUser?.email }}</div>
          <div class="text-sm mt-0.5" style="color:var(--gray-400);">
            <span class="badge" :class="roleBadge">{{ roleLabel }}</span>
            <span v-if="domainName" class="ml-2">{{ domainName }}</span>
          </div>
        </div>
      </div>

      <!-- Editable fields -->
      <div class="grid gap-4" style="grid-template-columns:1fr 1fr;">
        <div>
          <label class="block text-sm font-medium mb-1.5" style="color:var(--gray-700);">公司 / 組織名稱</label>
          <input v-model="form.company_name" class="input w-full" placeholder="輸入公司名稱…">
        </div>
        <div>
          <label class="block text-sm font-medium mb-1.5" style="color:var(--gray-700);">分機號碼</label>
          <input v-model="form.phone_ext" class="input w-full" placeholder="例：1234">
        </div>
      </div>

      <!-- Read-only info -->
      <div class="grid gap-3 pt-1" style="grid-template-columns:1fr 1fr;">
        <div class="rounded-lg p-3" style="background:var(--gray-50);border:1px solid var(--border);">
          <div class="text-xs mb-0.5" style="color:var(--gray-400);">Email / 登入帳號</div>
          <div class="text-sm font-medium truncate" style="color:var(--gray-700);">{{ currentUser?.username }}</div>
        </div>
        <div class="rounded-lg p-3" style="background:var(--gray-50);border:1px solid var(--border);">
          <div class="text-xs mb-0.5" style="color:var(--gray-400);">角色</div>
          <div class="text-sm font-medium" style="color:var(--gray-700);">{{ roleLabel }}</div>
        </div>
        <div v-if="currentUser?.department_id" class="rounded-lg p-3" style="background:var(--gray-50);border:1px solid var(--border);">
          <div class="text-xs mb-0.5" style="color:var(--gray-400);">部門 ID</div>
          <div class="text-sm font-medium" style="color:var(--gray-700);">{{ currentUser.department_id }}</div>
        </div>
        <div class="rounded-lg p-3" style="background:var(--gray-50);border:1px solid var(--border);">
          <div class="text-xs mb-0.5" style="color:var(--gray-400);">信箱配額</div>
          <div class="text-sm font-medium" style="color:var(--gray-700);">{{ currentUser?.quota ? (currentUser.quota / 1024 / 1024).toFixed(0) + ' MB' : '無限制' }}</div>
        </div>
      </div>

      <div v-if="saveError" class="text-sm p-3 rounded-lg" style="background:var(--color-danger-bg);color:var(--color-danger);">{{ saveError }}</div>
      <div v-if="saveSuccess" class="text-sm p-3 rounded-lg" style="background:var(--color-success-bg);color:var(--color-success);">{{ saveSuccess }}</div>

      <div class="flex justify-end">
        <button class="btn btn-primary" @click="saveProfile" :disabled="saving">
          {{ saving ? '儲存中…' : '儲存變更' }}
        </button>
      </div>
    </div>

    <!-- Appearance Card -->
    <div class="card p-6 space-y-4">
      <div class="font-semibold text-sm" style="color:var(--gray-500);letter-spacing:.05em;text-transform:uppercase;">外觀設定</div>

      <div class="flex items-center justify-between">
        <div>
          <div class="font-medium" style="color:var(--gray-800);">深色模式</div>
          <div class="text-sm mt-0.5" style="color:var(--gray-400);">切換全站夜間模式，偏好自動儲存</div>
        </div>
        <!-- Toggle switch -->
        <button
          @click="toggleDarkMode()"
          class="relative inline-flex items-center rounded-full transition-colors"
          :style="{ width: '44px', height: '24px', background: darkMode ? 'var(--brand-500)' : 'var(--gray-300)', flexShrink: 0 }"
        >
          <span
            class="inline-block rounded-full bg-white transition-transform"
            :style="{ width: '18px', height: '18px', transform: darkMode ? 'translateX(23px)' : 'translateX(3px)', boxShadow: 'var(--shadow-sm)' }"
          ></span>
        </button>
      </div>
    </div>

    <!-- Change Password Card -->
    <div class="card p-6 space-y-4">
      <div class="font-semibold text-sm" style="color:var(--gray-500);letter-spacing:.05em;text-transform:uppercase;">修改密碼</div>

      <form class="space-y-3" @submit.prevent="changePassword">
        <div>
          <label class="block text-sm font-medium mb-1.5" style="color:var(--gray-700);">目前密碼</label>
          <input v-model="pwForm.current_password" type="password" class="input w-full" placeholder="輸入目前密碼" autocomplete="current-password">
        </div>
        <div>
          <label class="block text-sm font-medium mb-1.5" style="color:var(--gray-700);">新密碼</label>
          <input v-model="pwForm.new_password" type="password" class="input w-full" placeholder="至少 8 個字元" autocomplete="new-password">
        </div>
        <div>
          <label class="block text-sm font-medium mb-1.5" style="color:var(--gray-700);">確認新密碼</label>
          <input v-model="pwForm.confirm_password" type="password" class="input w-full" placeholder="再次輸入新密碼" autocomplete="new-password">
        </div>

        <div v-if="pwError" class="text-sm p-3 rounded-lg" style="background:var(--color-danger-bg);color:var(--color-danger);">{{ pwError }}</div>
        <div v-if="pwSuccess" class="text-sm p-3 rounded-lg" style="background:var(--color-success-bg);color:var(--color-success);">{{ pwSuccess }}</div>

        <div class="flex justify-end">
          <button type="submit" class="btn btn-primary" :disabled="pwLoading">
            {{ pwLoading ? '更新中…' : '更新密碼' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Mail Signature Card -->
    <div class="card p-6 space-y-4">
      <div class="font-semibold text-sm" style="color:var(--gray-500);letter-spacing:.05em;text-transform:uppercase;">郵件簽名</div>

      <div class="flex items-center justify-between">
        <div>
          <div class="font-medium" style="color:var(--gray-800);">啟用郵件簽名</div>
          <div class="text-sm mt-0.5" style="color:var(--gray-400);">發信時自動附加簽名至郵件底部</div>
        </div>
        <button
          @click="mailSignature.enabled = !mailSignature.enabled"
          class="relative inline-flex items-center rounded-full transition-colors"
          :style="{ width: '44px', height: '24px', background: mailSignature.enabled ? 'var(--brand-500)' : 'var(--gray-300)', flexShrink: 0 }"
        >
          <span
            class="inline-block rounded-full bg-white transition-transform"
            :style="{ width: '18px', height: '18px', transform: mailSignature.enabled ? 'translateX(23px)' : 'translateX(3px)', boxShadow: 'var(--shadow-sm)' }"
          ></span>
        </button>
      </div>

      <div v-if="mailSignature.loading" class="text-sm" style="color:var(--gray-400);">載入中…</div>

      <template v-else>
        <div>
          <label class="block text-sm font-medium mb-1.5" style="color:var(--gray-700);">HTML 簽名</label>
          <textarea
            v-model="mailSignature.signature_html"
            class="input w-full font-mono text-xs"
            rows="6"
            placeholder="&lt;div&gt;Best regards,&lt;br&gt;&lt;strong&gt;您的姓名&lt;/strong&gt;&lt;/div&gt;"
          ></textarea>
          <div class="text-xs mt-1" style="color:var(--gray-400);">支援 HTML 標籤，郵件客戶端會渲染為富文字格式</div>
        </div>

        <div>
          <label class="block text-sm font-medium mb-1.5" style="color:var(--gray-700);">純文字簽名（備用）</label>
          <textarea
            v-model="mailSignature.signature_text"
            class="input w-full font-mono text-xs"
            rows="3"
            placeholder="Best regards,&#10;您的姓名"
          ></textarea>
        </div>

        <!-- Logo -->
        <div>
          <label class="block text-sm font-medium mb-1.5" style="color:var(--gray-700);">簽名 Logo</label>
          <div v-if="mailSignature.logoUrl" class="flex items-center gap-3 mb-2">
            <img :src="mailSignature.logoUrl" alt="Logo" class="h-10 rounded border" style="border-color:var(--border);object-fit:contain;">
            <button @click="deleteSignatureLogo()" class="btn btn-ghost btn-xs" style="color:var(--color-danger);">移除</button>
          </div>
          <label class="btn btn-secondary btn-sm cursor-pointer">
            <input type="file" accept="image/*" class="hidden" @change="handleLogoUpload">
            {{ mailSignature.logoUrl ? '更換 Logo' : '上傳 Logo' }}
          </label>
        </div>

        <!-- Preview -->
        <div v-if="mailSignature.signature_html" class="rounded-lg p-4" style="background:var(--gray-50);border:1px solid var(--border);">
          <div class="text-xs mb-2 font-semibold" style="color:var(--gray-400);">預覽</div>
          <div class="text-sm" v-html="mailSignature.signature_html"></div>
        </div>
      </template>

      <div class="flex justify-end">
        <button class="btn btn-primary" @click="saveMailSignature()" :disabled="mailSignature.saving">
          {{ mailSignature.saving ? '儲存中…' : '儲存簽名' }}
        </button>
      </div>
    </div>

    <!-- Security Settings Card -->
    <div class="card p-6 space-y-4">
      <div class="font-semibold text-sm" style="color:var(--gray-500);letter-spacing:.05em;text-transform:uppercase;">帳號安全設定</div>

      <!-- Login alert toggle -->
      <div class="flex items-center justify-between">
        <div>
          <div class="font-medium" style="color:var(--gray-800);">新 IP 登入警報</div>
          <div class="text-sm mt-0.5" style="color:var(--gray-400);">從未曾登入過的 IP 登入時，自動發送警示信至您的信箱</div>
        </div>
        <button
          @click="toggleLoginAlert()"
          class="relative inline-flex items-center rounded-full transition-colors"
          :style="{ width: '44px', height: '24px', background: security.login_alert_enabled ? 'var(--brand-500)' : 'var(--gray-300)', flexShrink: 0 }"
        >
          <span
            class="inline-block rounded-full bg-white transition-transform"
            :style="{ width: '18px', height: '18px', transform: security.login_alert_enabled ? 'translateX(23px)' : 'translateX(3px)', boxShadow: 'var(--shadow-sm)' }"
          ></span>
        </button>
      </div>

      <div class="rounded-lg p-3 flex items-center gap-3" style="background:var(--gray-50);border:1px solid var(--border);">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color:var(--gray-400);flex-shrink:0;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <div class="text-xs" style="color:var(--gray-500);">
          目前已記錄 <strong>{{ security.known_ip_count }}</strong> 個信任 IP ·
          TOTP 狀態：<strong>{{ security.totp_enabled ? '已啟用' : '未啟用' }}</strong>
        </div>
      </div>

      <!-- E4: IP 白名單 -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <div>
            <div class="font-medium" style="color:var(--gray-800);">登入 IP 白名單</div>
            <div class="text-sm mt-0.5" style="color:var(--gray-400);">
              限制只有指定 IP 才能登入此帳號（留空代表不限制）
              <span v-if="security.ip_whitelist_enabled" class="ml-1 text-xs px-1.5 py-0.5 rounded-full font-medium" style="background:var(--color-danger-bg);color:var(--color-danger);">已啟用</span>
            </div>
          </div>
          <button class="btn btn-secondary btn-sm" @click="ipWl.show=!ipWl.show">
            {{ ipWl.show ? '收起' : '管理' }}
          </button>
        </div>
        <div v-if="ipWl.show" class="space-y-3 p-4 rounded-lg" style="background:var(--gray-50);border:1px solid var(--border);">
          <div class="text-xs" style="color:var(--gray-500);">每行一個 IP（支援 IPv4/IPv6，支援 CIDR 例如 192.168.1.0/24）</div>
          <textarea v-model="ipWl.text" class="input w-full font-mono text-xs" rows="4"
            placeholder="192.168.1.0/24&#10;10.0.0.1&#10;（留空代表不限制任何 IP）"></textarea>
          <div v-if="ipWl.error" class="text-sm p-2 rounded" style="background:var(--color-danger-bg);color:var(--color-danger);">{{ ipWl.error }}</div>
          <div class="text-xs p-2 rounded" style="background:var(--color-warning-bg);color:var(--color-warning);">
            警告：設定後只有列出的 IP 才能登入，請確認已包含您目前的 IP，否則將被鎖帳號。
          </div>
          <div class="flex gap-2">
            <button class="btn btn-primary btn-sm" @click="saveIpWhitelist" :disabled="ipWl.saving">
              {{ ipWl.saving ? '儲存中…' : '儲存白名單' }}
            </button>
            <button v-if="security.ip_whitelist_enabled" class="btn btn-ghost btn-sm" @click="clearIpWhitelist">清除（解除限制）</button>
            <button class="btn btn-ghost btn-sm" @click="ipWl.show=false">取消</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 2FA / TOTP Card -->
    <div class="card p-6 space-y-4">
      <div class="font-semibold text-sm" style="color:var(--gray-500);letter-spacing:.05em;text-transform:uppercase;">雙因素驗證 (2FA)</div>

      <div class="flex items-center justify-between">
        <div>
          <div class="font-medium" style="color:var(--gray-800);">驗證器應用程式 (TOTP)</div>
          <div class="text-sm mt-0.5" style="color:var(--gray-400);">使用 Google Authenticator、Authy 等 App 增強帳號安全</div>
        </div>
        <span v-if="totp.enabled" class="badge badge-success">已啟用</span>
        <span v-else class="badge" style="background:var(--gray-100);color:var(--gray-500);">未啟用</span>
      </div>

      <!-- Setup flow: step 1 — show QR code -->
      <div v-if="totp.phase === 'setup'" class="space-y-4">
        <div class="rounded-lg p-4 text-center" style="background:var(--gray-50);border:1px solid var(--border);">
          <div class="text-xs mb-3 font-semibold" style="color:var(--gray-500);">1. 用驗證器 App 掃描 QR Code</div>
          <img :src="totp.qrCode" alt="TOTP QR Code" class="inline-block" style="max-width:180px;border-radius:8px;">
          <div class="mt-3 text-xs" style="color:var(--gray-400);">或手動輸入金鑰：</div>
          <code class="block mt-1 text-xs font-mono p-2 rounded" style="background:var(--surface);border:1px solid var(--border);color:var(--gray-700);word-break:break-all;">{{ totp.secret }}</code>
        </div>
        <div>
          <label class="block text-sm font-medium mb-1.5" style="color:var(--gray-700);">2. 輸入 App 顯示的 6 位驗證碼確認</label>
          <input v-model="totp.confirmCode" type="text" class="input w-full text-center font-mono text-lg tracking-widest" maxlength="8" placeholder="000000" inputmode="numeric">
        </div>
        <div v-if="totp.error" class="text-sm p-3 rounded-lg" style="background:var(--color-danger-bg);color:var(--color-danger);">{{ totp.error }}</div>
        <div class="flex gap-2">
          <button class="btn btn-primary" @click="totpConfirm" :disabled="totp.loading">{{ totp.loading ? '驗證中…' : '啟用 2FA' }}</button>
          <button class="btn btn-ghost" @click="totp.phase='idle'">取消</button>
        </div>
      </div>

      <!-- Setup flow: step 2 — show backup codes -->
      <div v-else-if="totp.phase === 'backup'" class="space-y-3">
        <div class="rounded-lg p-4" style="background:var(--color-success-bg);border:1px solid var(--color-success);">
          <div class="font-semibold text-sm mb-1" style="color:var(--color-success);">2FA 已成功啟用！</div>
          <div class="text-xs" style="color:var(--gray-600);">請妥善保存以下備用碼，每個備用碼只能使用一次：</div>
        </div>
        <div class="grid grid-cols-2 gap-2">
          <code v-for="code in totp.backupCodes" :key="code" class="block text-center p-2 rounded font-mono text-sm" style="background:var(--gray-100);border:1px solid var(--border);">{{ code }}</code>
        </div>
        <button class="btn btn-primary" @click="totp.phase='idle';totp.enabled=true">我已儲存備用碼</button>
      </div>

      <!-- Disable flow -->
      <form v-else-if="totp.phase === 'disable'" class="space-y-3" @submit.prevent="totpDisable">
        <div>
          <label class="block text-sm font-medium mb-1.5" style="color:var(--gray-700);">目前密碼</label>
          <input v-model="totp.disablePwd" type="password" class="input w-full" placeholder="輸入目前登入密碼" autocomplete="current-password">
        </div>
        <div>
          <label class="block text-sm font-medium mb-1.5" style="color:var(--gray-700);">驗證碼或備用碼</label>
          <input v-model="totp.disableCode" type="text" class="input w-full font-mono" placeholder="6 位驗證碼 或 備用碼" autocomplete="one-time-code">
        </div>
        <div v-if="totp.error" class="text-sm p-3 rounded-lg" style="background:var(--color-danger-bg);color:var(--color-danger);">{{ totp.error }}</div>
        <div class="flex gap-2">
          <button type="submit" class="btn btn-danger" :disabled="totp.loading">{{ totp.loading ? '停用中…' : '確認停用' }}</button>
          <button type="button" class="btn btn-ghost" @click="totp.phase='idle'">取消</button>
        </div>
      </form>

      <!-- Idle state -->
      <div v-else class="flex gap-2">
        <button v-if="!totp.enabled" class="btn btn-primary" @click="totpSetup">設定 2FA</button>
        <button v-else class="btn btn-outline" style="color:var(--color-danger);border-color:var(--color-danger);" @click="totp.phase='disable';totp.error=''">停用 2FA</button>
      </div>
    </div>

    <!-- PGP 金鑰管理 Card -->
    <div class="card p-6 space-y-4">
      <div class="font-semibold text-sm" style="color:var(--gray-500);letter-spacing:.05em;text-transform:uppercase;">PGP 公鑰管理</div>
      <div class="text-xs" style="color:var(--gray-400);">
        PGP 加密讓收件人無法在沒有私鑰的情況下讀取郵件內容。<strong>私鑰永不上傳伺服器</strong>，請自行妥善保管。
      </div>

      <!-- Key list -->
      <div v-if="pgp.loading" class="text-sm" style="color:var(--gray-400);">載入中…</div>
      <div v-else-if="pgp.keys.length > 0" class="space-y-2">
        <div v-for="key in pgp.keys" :key="key.fingerprint"
          class="flex items-start gap-3 p-3 rounded-lg" style="background:var(--gray-50);border:1px solid var(--border);">
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium" style="color:var(--gray-800);">{{ key.name || key.email }}</div>
            <div class="text-xs font-mono mt-0.5" style="color:var(--gray-400);">{{ key.fingerprint.match(/.{1,4}/g).join(' ') }}</div>
            <div class="text-xs mt-0.5" style="color:var(--gray-400);">
              Key ID: {{ key.key_id }} · 新增於 {{ fmtDateShort(key.created_at) }}
              <span v-if="key.expires_at"> · 到期：{{ fmtDateShort(key.expires_at) }}</span>
            </div>
          </div>
          <button class="btn btn-ghost btn-xs" style="color:var(--color-danger);" @click="deletePgpKey(key.fingerprint)">移除</button>
        </div>
      </div>
      <div v-else class="text-sm p-3 rounded-lg text-center" style="color:var(--gray-400);background:var(--gray-50);border:1px solid var(--border);">
        尚未上傳任何 PGP 公鑰
      </div>

      <!-- Generate or upload buttons — hidden when any panel is open -->
      <div v-if="!pgp.showUpload && !pgp.showGenerate" class="flex gap-2 flex-wrap">
        <button class="btn btn-primary" @click="generatePgpKey">生成金鑰對</button>
        <button class="btn btn-secondary" @click="pgp.showUpload=true;pgp.error=''">上傳既有公鑰</button>
      </div>

      <!-- Generate panel -->
      <div v-if="pgp.showGenerate" class="space-y-3 p-4 rounded-lg" style="background:var(--gray-50);border:1px solid var(--border);">
        <div class="font-medium text-sm" style="color:var(--gray-700);">生成新的 PGP 金鑰對</div>
        <div v-if="pgp.generating" class="flex items-center gap-2 text-sm" style="color:var(--gray-500);">
          <div class="spinner"></div> 生成中，請稍候…
        </div>
        <template v-else-if="pgp.generatedPriv">
          <div class="rounded-lg p-3" style="background:var(--color-warning-bg);border:1px solid var(--color-warning);">
            <div class="text-sm font-semibold mb-1" style="color:var(--color-warning);">私鑰已生成 — 請立即下載並安全保存！</div>
            <div class="text-xs" style="color:var(--gray-700);">私鑰將<strong>不會儲存至伺服器</strong>，遺失後無法還原。</div>
          </div>
          <!-- Show generated public key so user can verify -->
          <div>
            <label class="block text-xs font-medium mb-1" style="color:var(--gray-500);">已生成的公鑰（可複製）</label>
            <textarea readonly class="input w-full font-mono text-xs" rows="6"
              style="resize:none;background:var(--gray-100);color:var(--gray-700);"
              :value="pgp.generatedPub"></textarea>
          </div>
          <div class="flex gap-2 flex-wrap">
            <button class="btn btn-warning btn-sm" @click="downloadPrivKey">⬇ 下載私鑰 (.asc)</button>
            <button class="btn btn-primary btn-sm" @click="uploadGeneratedPubKey" :disabled="pgp.uploading">
              {{ pgp.uploading ? '上傳中…' : '上傳公鑰至伺服器' }}
            </button>
            <button class="btn btn-ghost btn-sm" @click="pgp.showGenerate=false;pgp.generatedPriv='';pgp.generatedPub='';pgp.error=''">關閉</button>
          </div>
        </template>
        <div v-if="pgp.error" class="text-sm p-2 rounded" style="background:var(--color-danger-bg);color:var(--color-danger);">{{ pgp.error }}</div>
      </div>

      <!-- Upload panel -->
      <div v-if="pgp.showUpload" class="space-y-3">
        <div>
          <label class="block text-sm font-medium mb-1.5" style="color:var(--gray-700);">貼上 ASCII-armored <strong>公鑰</strong>（不是私鑰）</label>
          <textarea v-model="pgp.uploadText" class="input w-full font-mono text-xs" rows="8"
            placeholder="-----BEGIN PGP PUBLIC KEY BLOCK-----&#10;...&#10;-----END PGP PUBLIC KEY BLOCK-----"></textarea>
          <div class="text-xs mt-1" style="color:var(--gray-400);">請確認是公鑰（BEGIN PGP PUBLIC KEY BLOCK），私鑰請勿上傳。</div>
        </div>
        <div v-if="pgp.error" class="text-sm p-2 rounded" style="background:var(--color-danger-bg);color:var(--color-danger);">{{ pgp.error }}</div>
        <div class="flex gap-2">
          <button class="btn btn-primary" @click="uploadPgpKey" :disabled="pgp.uploading">{{ pgp.uploading ? '上傳中…' : '上傳公鑰' }}</button>
          <button class="btn btn-ghost" @click="pgp.showUpload=false;pgp.uploadText='';pgp.error=''">取消</button>
        </div>
      </div>
    </div>

    <!-- Web Push Card -->
    <div class="card p-6 space-y-4">
      <div class="font-semibold text-sm" style="color:var(--gray-500);letter-spacing:.05em;text-transform:uppercase;">瀏覽器推播通知</div>
      <div class="text-sm" style="color:var(--gray-600);">啟用後，當有新郵件送達時，即使未開啟瀏覽器分頁也能收到桌面通知。</div>
      <div class="flex items-center gap-4">
        <button class="btn btn-primary btn-sm" v-if="!pushSubscribed" :disabled="pushLoading" @click="enablePush">
          <span v-if="pushLoading" class="spinner spinner-sm"></span>
          {{ pushLoading ? '訂閱中…' : '啟用推播通知' }}
        </button>
        <button class="btn btn-outline btn-sm" v-else :disabled="pushLoading" @click="disablePush" style="color:var(--color-danger);border-color:var(--color-danger);">
          <span v-if="pushLoading" class="spinner spinner-sm"></span>
          {{ pushLoading ? '取消中…' : '停用推播通知' }}
        </button>
        <span v-if="pushSubscribed" class="text-sm" style="color:var(--color-success);font-weight:600;">✓ 已啟用</span>
        <span v-if="pushError" class="text-sm" style="color:var(--color-danger);">{{ pushError }}</span>
      </div>
      <div v-if="pushDenied" class="text-xs p-2 rounded" style="background:var(--color-warning-bg);color:var(--color-warning);">
        瀏覽器已封鎖通知權限。請至瀏覽器設定 → 網站設定 → 通知，手動允許此網站。
      </div>
    </div>

    <!-- Session Info Card -->
    <div class="card p-6 space-y-3">
      <div class="font-semibold text-sm" style="color:var(--gray-500);letter-spacing:.05em;text-transform:uppercase;">目前工作階段</div>
      <div class="flex items-center justify-between p-3 rounded-lg" style="background:var(--gray-50);border:1px solid var(--border);">
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 rounded-full flex items-center justify-center" style="background:var(--color-success-bg);">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="color:var(--color-success);"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <div>
            <div class="text-sm font-medium" style="color:var(--gray-800);">{{ currentUser?.username }}</div>
            <div class="text-xs" style="color:var(--gray-400);">目前已登入 · {{ browserInfo }}</div>
          </div>
        </div>
        <span class="badge badge-success">使用中</span>
      </div>
      <div class="flex justify-end">
        <button class="btn btn-outline btn-sm" @click="logout()" style="color:var(--color-danger);border-color:var(--color-danger);">
          登出此裝置
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore, ADMIN_API } from '../store/app.js'
import axios from 'axios'
import * as openpgp from 'openpgp'

const store = useAppStore()
const { currentUser, darkMode, mailSignature } = storeToRefs(store)
const { toggleDarkMode, logout, fetchMailSignature, saveMailSignature, uploadSignatureLogo, deleteSignatureLogo } = store

const handleLogoUpload = (e) => {
  const file = e.target.files[0]
  if (file) uploadSignatureLogo(file)
  e.target.value = ''
}

// Profile form
const form = ref({ company_name: '', phone_ext: '' })
const saving = ref(false)
const saveError = ref('')
const saveSuccess = ref('')

// Password form
const pwForm = ref({ current_password: '', new_password: '', confirm_password: '' })
const pwLoading = ref(false)
const pwError = ref('')
const pwSuccess = ref('')

// Security settings
const security = ref({ login_alert_enabled: true, known_ip_count: 0, totp_enabled: false })

const toggleLoginAlert = async () => {
  const newVal = !security.value.login_alert_enabled
  try {
    await axios.post(`${ADMIN_API}/me/login-alert`, { enabled: newVal })
    security.value.login_alert_enabled = newVal
  } catch (e) {
    console.error('Failed to toggle login alert', e)
    window.$toast?.error('切換失敗', '無法更新登入告警設定')
  }
}

// E4: IP whitelist
const ipWl = ref({ show: false, text: '', saving: false, error: '' })

const saveIpWhitelist = async () => {
  ipWl.value.error = ''
  const ips = ipWl.value.text.split('\n').map(s => s.trim()).filter(Boolean)
  ipWl.value.saving = true
  try {
    const res = await axios.post(`${ADMIN_API}/me/ip-whitelist`, { ips })
    security.value.ip_whitelist = res.data.ips
    security.value.ip_whitelist_enabled = res.data.enabled
    ipWl.value.show = false
  } catch (e) {
    ipWl.value.error = e.response?.data?.detail || '儲存失敗'
  } finally {
    ipWl.value.saving = false
  }
}

const clearIpWhitelist = async () => {
  if (!confirm('確定解除 IP 白名單限制？所有 IP 都將可以登入。')) return
  ipWl.value.saving = true
  try {
    await axios.post(`${ADMIN_API}/me/ip-whitelist`, { ips: [] })
    security.value.ip_whitelist = []
    security.value.ip_whitelist_enabled = false
    ipWl.value.text = ''
    ipWl.value.show = false
  } catch (e) {
    ipWl.value.error = e.response?.data?.detail || '清除失敗'
  } finally {
    ipWl.value.saving = false
  }
}

// TOTP
const totp = ref({
  enabled: false,
  phase: 'idle',  // idle | setup | backup | disable
  loading: false,
  error: '',
  qrCode: '',
  secret: '',
  confirmCode: '',
  backupCodes: [],
  disablePwd: '',
  disableCode: '',
})

const avatarInitial = computed(() => {
  const u = currentUser.value?.username || currentUser.value?.email || 'U'
  return u[0].toUpperCase()
})

const domainName = computed(() => {
  const u = currentUser.value?.username || ''
  const at = u.indexOf('@')
  return at >= 0 ? u.slice(at + 1) : ''
})

const roleLabel = computed(() => {
  const map = { super_admin: '系統管理員', domain_admin: '網域管理員', user: '一般使用者' }
  return map[currentUser.value?.role] || currentUser.value?.role || '—'
})

const roleBadge = computed(() => {
  const map = { super_admin: 'badge-danger', domain_admin: 'badge-warning', user: 'badge-info' }
  return map[currentUser.value?.role] || ''
})

const browserInfo = computed(() => {
  const ua = navigator.userAgent
  if (ua.includes('Chrome')) return 'Chrome 瀏覽器'
  if (ua.includes('Firefox')) return 'Firefox 瀏覽器'
  if (ua.includes('Safari')) return 'Safari 瀏覽器'
  return '瀏覽器'
})

const saveProfile = async () => {
  saving.value = true
  saveError.value = ''
  saveSuccess.value = ''
  try {
    const res = await axios.patch(`${ADMIN_API}/users/me`, {
      company_name: form.value.company_name,
      phone_ext: form.value.phone_ext,
    })
    store.currentUser = res.data
    saveSuccess.value = '個人資料已更新'
    setTimeout(() => { saveSuccess.value = '' }, 3000)
  } catch (e) {
    saveError.value = e.response?.data?.detail || e.message || '儲存失敗'
  } finally {
    saving.value = false
  }
}

const changePassword = async () => {
  pwError.value = ''
  pwSuccess.value = ''
  if (!pwForm.value.current_password || !pwForm.value.new_password) {
    pwError.value = '請填寫所有欄位'
    return
  }
  if (pwForm.value.new_password !== pwForm.value.confirm_password) {
    pwError.value = '新密碼與確認密碼不一致'
    return
  }
  pwLoading.value = true
  try {
    await axios.post(`${ADMIN_API}/me/change-password`, {
      current_password: pwForm.value.current_password,
      new_password: pwForm.value.new_password,
    })
    pwSuccess.value = '密碼已成功更新'
    pwForm.value = { current_password: '', new_password: '', confirm_password: '' }
    setTimeout(() => { pwSuccess.value = '' }, 3000)
  } catch (e) {
    pwError.value = e.response?.data?.detail || e.message || '更新失敗'
  } finally {
    pwLoading.value = false
  }
}

const totpSetup = async () => {
  totp.value.error = ''
  totp.value.loading = true
  try {
    const res = await axios.post(`${ADMIN_API}/auth/totp/setup`)
    totp.value.qrCode = res.data.qr_code
    totp.value.secret = res.data.secret
    totp.value.confirmCode = ''
    totp.value.phase = 'setup'
  } catch (e) {
    totp.value.error = e.response?.data?.detail || '設定失敗'
  } finally {
    totp.value.loading = false
  }
}

const totpConfirm = async () => {
  totp.value.error = ''
  totp.value.loading = true
  try {
    const res = await axios.post(`${ADMIN_API}/auth/totp/confirm`, { code: totp.value.confirmCode })
    totp.value.backupCodes = res.data.backup_codes
    totp.value.phase = 'backup'
  } catch (e) {
    totp.value.error = e.response?.data?.detail || '驗證碼錯誤'
  } finally {
    totp.value.loading = false
  }
}

const totpDisable = async () => {
  totp.value.error = ''
  totp.value.loading = true
  try {
    await axios.post(`${ADMIN_API}/auth/totp/disable`, {
      current_password: totp.value.disablePwd,
      code: totp.value.disableCode,
    })
    totp.value.enabled = false
    totp.value.phase = 'idle'
    totp.value.disablePwd = ''
    totp.value.disableCode = ''
  } catch (e) {
    totp.value.error = e.response?.data?.detail || '停用失敗'
  } finally {
    totp.value.loading = false
  }
}

const fetchTotpStatus = async () => {
  try {
    const res = await axios.get(`${ADMIN_API}/auth/totp/status`)
    totp.value.enabled = res.data.totp_enabled
  } catch (e) {
    // ignore
  }
}

// PGP
const pgp = ref({
  keys: [],
  loading: false,
  showUpload: false,
  showGenerate: false,
  uploadText: '',
  uploading: false,
  generating: false,
  generatedPriv: '',
  generatedPub: '',
  generatedFp: '',
  generatedName: '',
  error: '',
})

const fmtDateShort = (d) => {
  if (!d) return '—'
  try { return new Date(d).toLocaleDateString('zh-TW') } catch { return d }
}

const fetchPgpKeys = async () => {
  pgp.value.loading = true
  try {
    const res = await axios.get(`${ADMIN_API}/users/me/pgp/keys`)
    pgp.value.keys = res.data.items || []
  } catch (e) {
    // ignore
  } finally {
    pgp.value.loading = false
  }
}

const generatePgpKey = async () => {
  pgp.value.error = ''
  pgp.value.showGenerate = true
  pgp.value.generating = true
  pgp.value.generatedPriv = ''
  pgp.value.generatedPub = ''
  try {
    const name = currentUser.value?.username?.split('@')[0] || 'User'
    const email = currentUser.value?.email || ''
    const { privateKey, publicKey } = await openpgp.generateKey({
      type: 'ecc',
      curve: 'curve25519',
      userIDs: [{ name, email }],
      format: 'armored',
    })
    // Extract fingerprint
    const pubKeyObj = await openpgp.readKey({ armoredKey: publicKey })
    const fp = pubKeyObj.getFingerprint().toUpperCase()
    const keyId = fp.slice(-16)
    pgp.value.generatedPriv = privateKey
    pgp.value.generatedPub = publicKey
    pgp.value.generatedFp = fp
    pgp.value.generatedName = name
  } catch (e) {
    pgp.value.error = '金鑰生成失敗：' + (e.message || e)
  } finally {
    pgp.value.generating = false
  }
}

const downloadPrivKey = () => {
  const blob = new Blob([pgp.value.generatedPriv], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  const email = (currentUser.value?.email || 'key').replace('@', '_at_')
  a.href = url
  a.download = `pgp_private_${email}.asc`
  a.click()
  URL.revokeObjectURL(url)
}

const uploadGeneratedPubKey = async () => {
  pgp.value.error = ''
  pgp.value.uploading = true
  try {
    const name = pgp.value.generatedName || ''
    const email = currentUser.value?.email || ''
    const fp = pgp.value.generatedFp
    await axios.post(`${ADMIN_API}/users/me/pgp/keys`, {
      public_key: pgp.value.generatedPub,
      fingerprint: fp,
      key_id: fp.slice(-16),
      name,
      email,
    })
    pgp.value.generatedPriv = ''
    pgp.value.generatedPub = ''
    pgp.value.showGenerate = false
    await fetchPgpKeys()
  } catch (e) {
    pgp.value.error = e.response?.data?.detail || '上傳失敗，請確認伺服器連線'
  } finally {
    pgp.value.uploading = false
  }
}

const uploadPgpKey = async () => {
  pgp.value.error = ''
  const armored = pgp.value.uploadText.trim()
  if (!armored) {
    pgp.value.error = '請先貼入 PGP 公鑰文字'
    return
  }
  if (armored.includes('BEGIN PGP PRIVATE KEY BLOCK') || armored.includes('BEGIN PGP SECRET KEY BLOCK')) {
    pgp.value.error = '您貼入的是私鑰，請貼入對應的公鑰（BEGIN PGP PUBLIC KEY BLOCK）'
    return
  }
  if (!armored.includes('BEGIN PGP PUBLIC KEY BLOCK')) {
    pgp.value.error = '格式不正確，請貼入完整的公鑰區塊（-----BEGIN PGP PUBLIC KEY BLOCK-----）'
    return
  }
  pgp.value.uploading = true
  try {
    const pubKeyObj = await openpgp.readKey({ armoredKey: armored })
    const fp = pubKeyObj.getFingerprint().toUpperCase()
    const uid = pubKeyObj.getUserIDs()[0] || ''
    const nameMatch = uid.match(/^(.+?)\s*</)
    const emailMatch = uid.match(/<(.+?)>/)
    const expiry = pubKeyObj.getExpirationTime()
    await axios.post(`${ADMIN_API}/users/me/pgp/keys`, {
      public_key: armored,
      fingerprint: fp,
      key_id: fp.slice(-16),
      name: nameMatch ? nameMatch[1].trim() : '',
      email: emailMatch ? emailMatch[1] : (currentUser.value?.email || ''),
      expires_at: expiry instanceof Date ? expiry.toISOString() : null,
    })
    pgp.value.uploadText = ''
    pgp.value.showUpload = false
    await fetchPgpKeys()
  } catch (e) {
    pgp.value.error = e.response?.data?.detail || e.message || '上傳失敗'
  } finally {
    pgp.value.uploading = false
  }
}

const deletePgpKey = async (fp) => {
  if (!confirm('確定要移除此公鑰嗎？')) return
  try {
    await axios.delete(`${ADMIN_API}/users/me/pgp/keys/${fp}`)
    await fetchPgpKeys()
  } catch (e) {
    window.$toast?.error('移除失敗', e.response?.data?.detail || '無法移除公鑰')
  }
}

// Web Push
const pushSubscribed = ref(false)
const pushLoading = ref(false)
const pushError = ref('')
const pushDenied = ref(false)

function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)))
}

async function enablePush() {
  pushError.value = ''
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    pushError.value = '此瀏覽器不支援推播通知'
    return
  }
  const perm = await Notification.requestPermission()
  if (perm === 'denied') { pushDenied.value = true; return }
  if (perm !== 'granted') return
  pushLoading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/me/vapid-public-key`)
    const vapidKey = res.data.key
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(vapidKey),
    })
    const j = sub.toJSON()
    await axios.post(`${ADMIN_API}/me/push-subscription`, {
      endpoint: j.endpoint,
      p256dh: j.keys.p256dh,
      auth: j.keys.auth,
    }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
    pushSubscribed.value = true
    window.$toast?.success('推播通知已啟用')
  } catch (e) {
    pushError.value = e?.response?.data?.detail || e.message || '訂閱失敗'
  } finally {
    pushLoading.value = false
  }
}

async function disablePush() {
  pushError.value = ''
  pushLoading.value = true
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    if (sub) {
      await axios.delete(`${ADMIN_API}/me/push-subscription`, {
        data: { endpoint: sub.endpoint },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      await sub.unsubscribe()
    }
    pushSubscribed.value = false
    window.$toast?.info('推播通知已停用')
  } catch (e) {
    pushError.value = e?.response?.data?.detail || e.message || '取消失敗'
  } finally {
    pushLoading.value = false
  }
}

async function checkPushStatus() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
  pushDenied.value = Notification.permission === 'denied'
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    pushSubscribed.value = !!sub
  } catch {}
}

const fetchSecurityStatus = async () => {
  try {
    const res = await axios.get(`${ADMIN_API}/me/security`)
    security.value = res.data
    totp.value.enabled = res.data.totp_enabled
  } catch (e) {
    // ignore
  }
}

onMounted(() => {
  if (currentUser.value) {
    form.value.company_name = currentUser.value.company_name || ''
    form.value.phone_ext = currentUser.value.phone_ext || ''
  }
  fetchMailSignature()
  fetchSecurityStatus()
  fetchPgpKeys()
  checkPushStatus()
})
</script>
